import { TonConnectUI, type Wallet } from '@tonconnect/ui'

export class TonConnectManager {
  private tonConnectUI: TonConnectUI
  private walletAddress: string | null = null
  private isConnected: boolean = false
  private isStoredInDatabase: boolean = false
  private isProcessingRemoval: boolean = false

  constructor() {
    this.tonConnectUI = new TonConnectUI({
      manifestUrl: `https://xian-network.github.io/xian-cookies/tonconnect-manifest.json`,
      buttonRootId: 'ton-connect-button'
    })

    this.setupEventListeners()
    this.checkConnection()
    this.checkStoredWallet()

    const ensureDisconnectButtonSetup = () => this.setupDisconnectButton()

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ensureDisconnectButtonSetup, { once: true })
    } else {
      ensureDisconnectButtonSetup()
    }
  }

  private convertToUserFriendlyAddress(rawAddress: string): string {
    // Backend now accepts both raw format (0:hex) and user-friendly format (EQ/UQ)
    // So we can just return the address as-is from TON Connect
    console.log('TON Connect provided address:', rawAddress)
    return rawAddress
  }

  private setupEventListeners(): void {
    this.tonConnectUI.onStatusChange((wallet: Wallet | null) => {
      if (wallet) {
        this.walletAddress = this.convertToUserFriendlyAddress(wallet.account.address)
        this.isConnected = true
        this.onWalletConnected(wallet)
      } else {
        this.walletAddress = null
        this.isConnected = false
        this.onWalletDisconnected()
      }
      this.updateUI()
    })
  }

  private async checkConnection(): Promise<void> {
    const wallet = this.tonConnectUI.wallet
    if (wallet) {
      this.walletAddress = this.convertToUserFriendlyAddress(wallet.account.address)
      this.isConnected = true
      this.onWalletConnected(wallet)
    }
    this.updateUI()
  }

  private onWalletConnected(wallet: Wallet): void {
    const userFriendlyAddress = this.convertToUserFriendlyAddress(wallet.account.address)
    console.log('Wallet connected:', userFriendlyAddress)
    
    // Store wallet address in backend
    this.storeWalletAddress(userFriendlyAddress)
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('walletConnected', {
      detail: { address: userFriendlyAddress, wallet }
    }))
  }

  private onWalletDisconnected(): void {
    console.log('Wallet disconnected')

    // Note: We don't reset isStoredInDatabase here because the wallet address
    // should remain stored in the database even if TON Connect is disconnected

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('walletDisconnected'))
  }

  private async removeStoredWallet(): Promise<void> {
    if (this.isProcessingRemoval) {
      return
    }

    // Get user data from Telegram WebApp
    const initData = (window as any).Telegram?.WebApp?.initData
    if (!initData) {
      console.warn('No Telegram init data available')
      return
    }

    // Extract user ID from initData
    const urlParams = new URLSearchParams(initData)
    const userJson = urlParams.get('user')
    if (!userJson) {
      console.warn('No user data in initData')
      return
    }

    const user = JSON.parse(userJson)
    const userId = user.id
    console.log('Removing stored wallet for user ID:', userId)

    this.isProcessingRemoval = true
    this.updateUI()

    try {
      const response = await fetch(`https://fortune.poc.workers.dev/remove-wallet/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TG-Init': initData
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('API error response:', errorData)
        throw new Error(`Failed to remove stored wallet: ${errorData.error || response.statusText}`)
      }

      const result = await response.json().catch(() => ({}))
      console.log('Wallet address removed successfully:', result)

      this.walletAddress = null
      this.isStoredInDatabase = false
      this.updateUI()

      window.dispatchEvent(new CustomEvent('walletRemoved'))
    } catch (error) {
      console.error('Error removing stored wallet:', error)
    } finally {
      this.isProcessingRemoval = false
      this.updateUI()
    }
  }

  private async checkStoredWallet(): Promise<void> {
    try {
      // Get user data from Telegram WebApp
      const initData = (window as any).Telegram?.WebApp?.initData
      if (!initData) {
        console.warn('No Telegram init data available')
        return
      }

      // Extract user ID from initData
      const urlParams = new URLSearchParams(initData)
      const userJson = urlParams.get('user')
      if (!userJson) {
        console.warn('No user data in initData')
        return
      }

      const user = JSON.parse(userJson)
      const userId = user.id

      // Check if user already has a stored wallet address
      const response = await fetch(`https://fortune.poc.workers.dev/status/${userId}`, {
        method: 'GET',
        headers: {
          'X-TG-Init': initData
        }
      })

      if (response.ok) {
        const userData = await response.json()
        if (userData.wallet_address) {
          // User already has a wallet stored in database
          this.walletAddress = userData.wallet_address
          this.isStoredInDatabase = true
          console.log('Found stored wallet address:', userData.wallet_address)
          this.updateUI()
        }
      }
    } catch (error) {
      console.error('Error checking stored wallet:', error)
    }
  }

  private async storeWalletAddress(address: string): Promise<void> {
    try {
      console.log('Attempting to store wallet address:', address)
      
      // Get user data from Telegram WebApp
      const initData = (window as any).Telegram?.WebApp?.initData
      if (!initData) {
        console.warn('No Telegram init data available')
        return
      }

      // Extract user ID from initData
      const urlParams = new URLSearchParams(initData)
      const userJson = urlParams.get('user')
      if (!userJson) {
        console.warn('No user data in initData')
        return
      }

      const user = JSON.parse(userJson)
      const userId = user.id
      console.log('Storing wallet for user ID:', userId)

      const requestBody = { walletAddress: address }
      console.log('Request body:', requestBody)

      // Store wallet address via backend API
      const response = await fetch(`https://fortune.poc.workers.dev/store-wallet/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TG-Init': initData
        },
        body: JSON.stringify(requestBody)
      })

      console.log('API response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('API error response:', errorData)
        throw new Error(`Failed to store wallet address: ${errorData.error || response.statusText}`)
      }

      const result = await response.json()
      console.log('Wallet address stored successfully:', result)
      
      // Mark as stored in database after successful API call
      this.isStoredInDatabase = true
      this.updateUI()
    } catch (error) {
      console.error('Error storing wallet address:', error)
    }
  }

  private updateUI(): void {
    const connectButton = document.getElementById('ton-connect-button')
    const walletInfo = document.getElementById('wallet-info')
    const walletAddress = document.getElementById('wallet-address')
    const disconnectButton = document.getElementById('disconnect-wallet')

    // Show connect button only if no wallet is stored in database
    if (connectButton) {
      connectButton.style.display = this.isStoredInDatabase ? 'none' : 'block'
    }

    // Show wallet info only if wallet is stored in database
    if (walletInfo) {
      walletInfo.style.display = this.isStoredInDatabase ? 'block' : 'none'
    }

    if (walletAddress && this.walletAddress) {
      // Format address for display (show first 6 and last 4 characters)
      const formattedAddress = `${this.walletAddress.slice(0, 6)}...${this.walletAddress.slice(-4)}`
      walletAddress.textContent = formattedAddress
    }

    if (disconnectButton) {
      disconnectButton.style.display = this.isStoredInDatabase ? 'block' : 'none'
      disconnectButton.setAttribute('aria-disabled', this.isProcessingRemoval ? 'true' : 'false')
      disconnectButton.toggleAttribute('disabled', this.isProcessingRemoval)
    }
  }

  public async connectWallet(): Promise<void> {
    try {
      await this.tonConnectUI.connectWallet()
    } catch (error) {
      console.error('Error connecting wallet:', error)
    }
  }

  public async disconnectWallet(): Promise<void> {
    try {
      if (this.tonConnectUI.wallet) {
        await this.tonConnectUI.disconnect()
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error)
    } finally {
      await this.removeStoredWallet()
    }
  }

  public getWalletAddress(): string | null {
    return this.walletAddress
  }

  public isWalletConnected(): boolean {
    return this.isConnected
  }

  public isWalletStored(): boolean {
    return this.isStoredInDatabase
  }

  public setupDisconnectButton(): void {
    const disconnectButton = document.getElementById('disconnect-wallet') as HTMLButtonElement | null

    if (!disconnectButton) {
      console.warn('Disconnect wallet button not found in DOM')
      return
    }

    if (disconnectButton.dataset.listenerAttached === 'true') {
      return
    }

    disconnectButton.addEventListener('click', () => {
      this.disconnectWallet()
    })

    disconnectButton.dataset.listenerAttached = 'true'
  }
}
